<template>
    <div class="pivot-color-settings-panel">
        <a-tabs v-model="activeTab">
            <!-- Heat Map Tab -->
            <a-tab-pane key="heatmap" :tab="$t('table.heatMap')">
                <div class="settings-section">
                    <a-form-item>
                        <a-checkbox
                            :checked="settings.heatMap.enabled"
                            @change="onHeatMapEnabledChange"
                        >
                            {{ $t('table.enableHeatMap') }}
                        </a-checkbox>
                    </a-form-item>

                    <template v-if="settings.heatMap.enabled">
                        <a-form-item>
                            <a-checkbox
                                :checked="settings.heatMap.ignoreZero !== false"
                                @change="onIgnoreZeroChange"
                            >
                                {{ $t('table.doNotColorZeroAndEmpty') }}
                            </a-checkbox>
                        </a-form-item>
                        <a-form-item :label="$t('table.colorScale')">
                            <a-select
                                :value="settings.heatMap.scale"
                                @change="onScaleChange"
                                style="width: 100%"
                            >
                                <a-select-option 
                                    v-for="(scale, key) in colorScales" 
                                    :key="key" 
                                    :value="key"
                                >
                                    {{ scale.name }}
                                </a-select-option>
                            </a-select>
                        </a-form-item>

                        <div class="color-preview">
                            <div class="preview-label">{{ $t('table.colorPreview') }}:</div>
                            <div class="gradient-bar" :style="gradientStyle"></div>
                            <div class="gradient-labels">
                                <span>{{ $t('table.min') }}</span>
                                <span>{{ $t('table.max') }}</span>
                            </div>
                        </div>

                        <a-divider>{{ $t('table.orCustomColors') }}</a-divider>

                        <a-form-item :label="$t('table.minValueColor')">
                            <a-input
                                :value="settings.heatMap.minColor"
                                @change="onMinColorChange"
                                type="color"
                                style="width: 80px"
                            />
                        </a-form-item>

                        <a-form-item :label="$t('table.maxValueColor')">
                            <a-input
                                :value="settings.heatMap.maxColor"
                                @change="onMaxColorChange"
                                type="color"
                                style="width: 80px"
                            />
                        </a-form-item>
                    </template>
                </div>
            </a-tab-pane>

            <!-- Manual Rules Tab -->
            <a-tab-pane key="manual" :tab="$t('table.manualRules')">
                <div class="settings-section">
                    <div class="rules-list">
                        <div 
                            v-for="(rule, index) in settings.manualRules" 
                            :key="'rule-' + index"
                            class="rule-item"
                        >
                            <div class="rule-header">
                                <span class="rule-index">{{ $t('table.rule') }} {{ index + 1 }}</span>
                                <a-button 
                                    type="link" 
                                    size="small"
                                    @click="removeRule(index)"
                                >
                                    <i class="material-icons">delete</i>
                                </a-button>
                            </div>

                            <div class="rule-config">
                                <a-form-item :label="$t('table.condition')">
                                    <a-select
                                        :value="rule.condition.operator"
                                        @change="(val) => onOperatorChange(index, val)"
                                        style="width: 100px"
                                    >
                                        <a-select-option value=">">></a-select-option>
                                        <a-select-option value=">=">>=</a-select-option>
                                        <a-select-option value="<"><</a-select-option>
                                        <a-select-option value="<="><=</a-select-option>
                                        <a-select-option value="=">=</a-select-option>
                                        <a-select-option value="!=">!=</a-select-option>
                                        <a-select-option value="between">{{ $t('table.between') }}</a-select-option>
                                    </a-select>
                                </a-form-item>

                                <a-form-item 
                                    v-if="rule.condition.operator !== 'between'" 
                                    :label="$t('table.value')"
                                >
                                    <a-input-number
                                        :value="rule.condition.value"
                                        @change="(val) => onConditionValueChange(index, val)"
                                        style="width: 100%"
                                    />
                                </a-form-item>

                                <template v-else>
                                    <a-form-item :label="$t('table.minValue')">
                                        <a-input-number
                                            :value="rule.condition.minValue"
                                            @change="(val) => onConditionMinValueChange(index, val)"
                                            style="width: 100%"
                                        />
                                    </a-form-item>
                                    <a-form-item :label="$t('table.maxValue')">
                                        <a-input-number
                                            :value="rule.condition.maxValue"
                                            @change="(val) => onConditionMaxValueChange(index, val)"
                                            style="width: 100%"
                                        />
                                    </a-form-item>
                                </template>

                                <a-form-item :label="$t('table.color')">
                                    <a-input
                                        :value="rule.color"
                                        @change="(e) => onRuleColorChange(index, e.target.value)"
                                        type="color"
                                        style="width: 80px"
                                    />
                                </a-form-item>
                            </div>
                        </div>
                    </div>

                    <a-button 
                        type="dashed" 
                        block 
                        @click="addRule"
                    >
                        <i class="material-icons">add</i>
                        {{ $t('table.addNewRule') }}
                    </a-button>
                </div>
            </a-tab-pane>
        </a-tabs>
    </div>
</template>

<script>
import Vue from "vue";
import PivotColorEngine from "../utils/PivotColorEngine";

export default Vue.extend({
    name: "PivotColorSettingsPanel",
    props: {
        value: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            activeTab: "heatmap",
            settings: this.value || this.getDefaultSettings(),
            colorScales: PivotColorEngine.COLOR_SCALES
        };
    },
    computed: {
        gradientStyle() {
            const scale = this.settings.heatMap.scale;
            const colors = this.colorScales[scale].colors;
            return {
                background: `linear-gradient(to right, ${colors.join(', ')})`
            };
        }
    },
    methods: {
        getDefaultSettings() {
            return {
                heatMap: {
                    enabled: false,
                    scale: 'green-yellow-red',
                    minColor: '#00ff00',
                    maxColor: '#ff0000',
                    ignoreZero: true
                },
                manualRules: []
            };
        },
        onHeatMapEnabledChange(e) {
            this.settings.heatMap.enabled = e.target.checked;
            this.emitChange();
        },
        onIgnoreZeroChange(e) {
            this.settings.heatMap.ignoreZero = e.target.checked;
            this.emitChange();
        },
        onScaleChange(value) {
            this.settings.heatMap.scale = value;
            this.emitChange();
        },
        onMinColorChange(e) {
            this.settings.heatMap.minColor = e.target.value;
            this.emitChange();
        },
        onMaxColorChange(e) {
            this.settings.heatMap.maxColor = e.target.value;
            this.emitChange();
        },
        addRule() {
            if (!this.settings.manualRules) {
                this.settings.manualRules = [];
            }
            this.settings.manualRules.push({
                condition: {
                    operator: '>',
                    value: 0
                },
                color: '#ff0000'
            });
            this.emitChange();
        },
        removeRule(index) {
            this.settings.manualRules.splice(index, 1);
            this.emitChange();
        },
        onOperatorChange(index, value) {
            this.settings.manualRules[index].condition.operator = value;
            if (value === 'between') {
                this.settings.manualRules[index].condition.minValue = 0;
                this.settings.manualRules[index].condition.maxValue = 100;
            }
            this.emitChange();
        },
        onConditionValueChange(index, value) {
            this.settings.manualRules[index].condition.value = value;
            this.emitChange();
        },
        onConditionMinValueChange(index, value) {
            this.settings.manualRules[index].condition.minValue = value;
            this.emitChange();
        },
        onConditionMaxValueChange(index, value) {
            this.settings.manualRules[index].condition.maxValue = value;
            this.emitChange();
        },
        onRuleColorChange(index, value) {
            this.settings.manualRules[index].color = value;
            this.emitChange();
        },
        emitChange() {
            this.$emit('input', this.settings);
            this.$emit('change', this.settings);
        }
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal) {
                    this.settings = Object.assign({}, this.getDefaultSettings(), newVal);
                }
            },
            deep: true
        }
    }
});
</script>

<style scoped lang="scss">
.pivot-color-settings-panel {
    .settings-section {
        padding: 12px 0;
    }

    .color-preview {
        margin: 16px 0;

        .preview-label {
            font-size: 13px;
            color: #666;
            margin-bottom: 8px;
        }

        .gradient-bar {
            height: 40px;
            border-radius: 4px;
            border: 1px solid #e8e8e8;
        }

        .gradient-labels {
            display: flex;
            justify-content: space-between;
            margin-top: 4px;
            font-size: 12px;
            color: #999;
        }
    }

    .rules-list {
        max-height: 400px;
        overflow-y: auto;
        margin-bottom: 12px;

        .rule-item {
            border: 1px solid #e8e8e8;
            border-radius: 4px;
            padding: 12px;
            margin-bottom: 12px;
            background: #fafafa;

            .rule-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;

                .rule-index {
                    font-weight: 600;
                    font-size: 13px;
                    color: #333;
                }
            }

            .rule-config {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }
        }
    }
}
</style>

